import isString from 'lodash/isString';
import keyBy from 'lodash/keyBy';
import pick from 'lodash/pick';
import upperFirst from 'lodash/upperFirst';
import type { ArdoqId, FutureStateClaim } from '@ardoq/api-types';
import type { Maybe } from '@ardoq/common-helpers';
import {
  claimsWithMissingGroup,
  discardDraft,
  REFERENCE_PROPERTIES,
  remapClaimIds,
  toDraftAliasOf,
  transition,
  withNextDraftVersion,
  writeDraft,
  type Drafted,
  type DraftAlias,
  type DraftEntities,
  type DraftOp,
  type DraftRejection,
  type DraftEntityKind,
  type FutureStateDrafts,
  type PersistedClaims,
} from 'transformations/futureStatePage/futureStateDrafts';
import type { ActiveFutureStateIds } from 'transformations/futureStatePage/futureStatePage$';
import type { CommandValidationResult } from 'aiControlledCommands/defineAiControlledCommand';
import type { CommandRejection } from 'aiControlledCommands/types';
import { isDraftId } from '../draftClaimId';
import type {
  ClaimCreation,
  ClaimUpdate,
  DraftClaimChange,
  DraftClaimGroupChange,
  DraftFutureStateChangesPayload,
} from './command';

export const MAX_CLAIM_DRAFTS = 200;

export type FutureStateDraftExecutionState = {
  key: ActiveFutureStateIds;
  drafts: FutureStateDrafts;
  persisted: Maybe<PersistedClaims>;
};

type DraftChangeInputs = {
  newId: () => ArdoqId;
  now: string;
};

type Applied = CommandValidationResult<FutureStateDrafts>;

type PersistedById = {
  [Kind in DraftEntityKind]: Partial<Record<ArdoqId, DraftEntities[Kind]>>;
};

type DraftsByEntityKind = {
  [Kind in DraftEntityKind]: Partial<
    Record<ArdoqId, Drafted<DraftEntities[Kind]>>
  >;
};

type Resolve = (alias: DraftAlias) => ArdoqId;

type ChangeContext = DraftChangeInputs & {
  resolve: Resolve;
  futureStateId: ArdoqId;
};

type EntityChange<Kind extends DraftEntityKind> = {
  op: DraftOp;
  alias: DraftAlias;
  id: ArdoqId;
  toEntity: (
    draft: DraftEntities[Kind] | undefined,
    persisted: DraftEntities[Kind] | undefined
  ) => DraftEntities[Kind] | undefined;
};

const ENTITY_LABELS: Record<DraftEntityKind, string> = {
  claims: 'claim',
  claimGroups: 'claim group',
};

const REJECTION_REASONS: Record<
  DraftRejection,
  (label: string, alias: DraftAlias) => string
> = {
  noDraft: (label, alias) => `No draft exists for ${label} "${alias}".`,
  deleteDraft: (label, alias) =>
    `${upperFirst(label)} "${alias}" has a delete draft. Discard the delete draft before you update the ${label}.`,
};

const reject = (reason: string): CommandRejection => ({
  status: 'rejected',
  reason,
});

const valid = (payload: FutureStateDrafts): Applied => ({
  status: 'valid',
  payload,
});

const reasonFor = <Found>(
  found: Found | undefined,
  reason: (found: Found) => string
): string | null => (found === undefined ? null : reason(found));

const firstRejection = (
  checks: (() => string | null)[]
): CommandRejection | null => {
  const reason = checks.reduce<string | null>(
    (found, check) => found ?? check(),
    null
  );
  return reason === null ? null : reject(reason);
};

const aliasOf = (change: DraftClaimChange | DraftClaimGroupChange): string[] =>
  'alias' in change && change.alias !== undefined ? [change.alias] : [];

const referencedAliasesOf = (
  change: DraftClaimChange | DraftClaimGroupChange
): unknown[] =>
  Object.values({
    ...pick(change, ['claimId', 'claimGroupId', 'targetId']),
    ...pick('assertProperties' in change ? change.assertProperties : {}, [
      ...REFERENCE_PROPERTIES,
    ]),
  });

const setsRootWorkspace = (change: DraftClaimChange): change is ClaimUpdate =>
  change.kind === 'update' &&
  Object.hasOwn(change.assertProperties ?? {}, 'rootWorkspace');

const batchRejection = (
  payload: DraftFutureStateChangesPayload,
  drafts: FutureStateDrafts
): CommandRejection | null => {
  const changes = [...payload.claimGroups, ...payload.claims];
  const newAliases = changes.flatMap(aliasOf);
  return firstRejection([
    () =>
      drafts.accepting
        ? 'An accept is in progress. Try again when it has finished.'
        : null,
    () =>
      reasonFor(
        newAliases.find((alias, index) => newAliases.indexOf(alias) !== index),
        alias => `The alias "${alias}" is defined twice.`
      ),
    () =>
      reasonFor(
        newAliases.find(alias => Object.hasOwn(drafts.draftIdByAlias, alias)),
        alias => `The alias "${alias}" is already in use.`
      ),
    () =>
      reasonFor(
        payload.claims.find(setsRootWorkspace),
        ({ claimId }) =>
          `The update of claim "${claimId}" cannot set rootWorkspace.`
      ),
    () =>
      reasonFor(
        changes
          .flatMap(referencedAliasesOf)
          .filter(isString)
          .find(
            alias =>
              !newAliases.includes(alias) &&
              !Object.hasOwn(drafts.draftIdByAlias, alias) &&
              !isDraftId(alias)
          ),
        alias => `Unknown alias "${alias}".`
      ),
  ]);
};

const danglingGroupReason = (
  next: FutureStateDrafts,
  persistedGroups: PersistedById['claimGroups'],
  draftIdByAlias: FutureStateDrafts['draftIdByAlias']
): string | null => {
  const dangling = claimsWithMissingGroup(
    next,
    new Set(Object.keys(persistedGroups))
  );
  if (dangling.length === 0) return null;
  const toAlias = toDraftAliasOf(draftIdByAlias);
  const uses = dangling
    .map(
      ({ _id, claimGroupId }) =>
        `claim "${toAlias(_id)}" in group "${toAlias(claimGroupId ?? '')}"`
    )
    .join(', ');
  return (
    `Move these claims out of their claim group before you remove it: ${uses}. ` +
    "In the same batch, update each claim's claimGroupId (to another group or null) or discard its draft."
  );
};

const parentReasonOf = (
  {
    targetId,
    entityType,
    assertProperties,
    draftKind,
  }: Drafted<FutureStateClaim>,
  alias: string
): string | null => {
  const parent = assertProperties?.parent;
  if (isString(targetId)) {
    return Object.hasOwn(assertProperties ?? {}, 'parent')
      ? `Claim "${alias}" has a targetId, so it cannot set parent.`
      : null;
  }
  const mustSetParent =
    draftKind === 'create' || Object.hasOwn(assertProperties ?? {}, 'parent');
  return mustSetParent &&
    entityType === 'component' &&
    parent !== null &&
    !isString(parent)
    ? `Claim "${alias}" must set parent to an id or null.`
    : null;
};

const parentRuleReason = (
  previous: FutureStateDrafts,
  next: FutureStateDrafts,
  draftIdByAlias: FutureStateDrafts['draftIdByAlias']
): string | null => {
  const toAlias = toDraftAliasOf(draftIdByAlias);
  return (
    Object.values(next.claims)
      .filter(
        claim =>
          claim.draftKind !== 'delete' && previous.claims[claim._id] !== claim
      )
      .map(claim => parentReasonOf(claim, toAlias(claim._id)))
      .find(isString) ?? null
  );
};

const resultRejection = (
  previous: FutureStateDrafts,
  next: FutureStateDrafts,
  persistedGroups: PersistedById['claimGroups'],
  draftIdByAlias: FutureStateDrafts['draftIdByAlias']
): CommandRejection | null =>
  firstRejection([
    () => danglingGroupReason(next, persistedGroups, draftIdByAlias),
    () => parentRuleReason(previous, next, draftIdByAlias),
    () => {
      const count = Object.keys(next.claims).length;
      return count > MAX_CLAIM_DRAFTS
        ? `The changes leave ${count} claim drafts, more than the limit of ${MAX_CLAIM_DRAFTS}.`
        : null;
    },
  ]);

const removal = <Kind extends DraftEntityKind>(
  op: 'delete' | 'discard',
  alias: DraftAlias,
  resolve: Resolve
): EntityChange<Kind> => ({
  op,
  alias,
  id: resolve(alias),
  toEntity: (draft, persisted) => persisted ?? draft,
});

const updating = <Kind extends DraftEntityKind>(
  alias: DraftAlias,
  resolve: Resolve,
  merge: (current: DraftEntities[Kind]) => DraftEntities[Kind]
): EntityChange<Kind> => ({
  op: 'update',
  alias,
  id: resolve(alias),
  toEntity: (draft, persisted) => {
    const current = draft ?? persisted;
    return current && merge(current);
  },
});

const toGroupChange = (
  change: DraftClaimGroupChange,
  { resolve, futureStateId }: ChangeContext
): EntityChange<'claimGroups'> => {
  if (change.kind === 'create') {
    const id = resolve(change.alias);
    return {
      op: 'create',
      alias: change.alias,
      id,
      toEntity: () => ({
        _id: id,
        _version: 0,
        futureStateId,
        ...pick(change, ['name', 'description']),
      }),
    };
  }
  if (change.kind === 'update') {
    return updating<'claimGroups'>(change.claimGroupId, resolve, current => ({
      ...current,
      ...pick(change, ['name', 'description']),
    }));
  }
  return removal(change.kind, change.claimGroupId, resolve);
};

type ClaimBase = {
  _id: ArdoqId;
  _version: number;
  futureStateId: ArdoqId;
  created: string;
};

const claimFieldsOf = (
  change: ClaimCreation,
  base: ClaimBase
): FutureStateClaim => {
  switch (change.kind) {
    case 'createComponent':
      return {
        ...base,
        claimType: 'presence',
        entityType: 'component',
        claimGroupId: change.claimGroupId,
        assertProperties: change.assertProperties,
      };
    case 'createReference':
      return {
        ...base,
        claimType: 'presence',
        entityType: 'reference',
        claimGroupId: change.claimGroupId,
        assertProperties: change.assertProperties,
      };
    case 'create':
      return {
        ...base,
        claimType: change.claimType,
        entityType: change.entityType,
        targetId: change.targetId,
        claimGroupId: change.claimGroupId,
        assertProperties: change.assertProperties,
      };
  }
};

const toClaimCreation = (
  change: ClaimCreation,
  { resolve, futureStateId, newId, now }: ChangeContext
): EntityChange<'claims'> => {
  const { alias } = change;
  const id = alias === undefined ? newId() : resolve(alias);
  const base = { _id: id, _version: 0, futureStateId, created: now };
  return {
    op: 'create',
    alias: alias ?? id,
    id,
    toEntity: () => remapClaimIds(claimFieldsOf(change, base), resolve),
  };
};

const mergeClaim = <CurrentClaim extends FutureStateClaim>(
  current: CurrentClaim,
  change: ClaimUpdate
): CurrentClaim => ({
  ...current,
  ...pick(change, 'claimGroupId'),
  assertProperties: change.assertProperties
    ? { ...current.assertProperties, ...change.assertProperties }
    : current.assertProperties,
});

const toClaimChange = (
  change: DraftClaimChange,
  context: ChangeContext
): EntityChange<'claims'> => {
  const { resolve } = context;
  switch (change.kind) {
    case 'create':
    case 'createComponent':
    case 'createReference':
      return toClaimCreation(change, context);
    case 'update':
      return updating<'claims'>(change.claimId, resolve, current =>
        remapClaimIds(mergeClaim(current, change), resolve)
      );
    case 'delete':
    case 'discard':
      return removal(change.kind, change.claimId, resolve);
  }
};

const applyChange =
  <Kind extends DraftEntityKind>(
    entityKind: Kind,
    persisted: PersistedById[Kind],
    { op, alias, id, toEntity }: EntityChange<Kind>
  ) =>
  (drafts: FutureStateDrafts): Applied => {
    const draftsByKind: DraftsByEntityKind = drafts;
    const draft = draftsByKind[entityKind][id];
    const next = transition(draft?.draftKind, op);
    if (!next.ok) {
      return reject(
        REJECTION_REASONS[next.reason](ENTITY_LABELS[entityKind], alias)
      );
    }
    if (next.kind === null) {
      return valid(discardDraft(drafts, entityKind, id));
    }
    const entity = toEntity(draft, persisted[id]);
    return entity
      ? valid(writeDraft(drafts, entityKind, entity, next.kind))
      : reject(`Unknown ${ENTITY_LABELS[entityKind]} "${alias}".`);
  };

export const applyDraftChanges = (
  payload: DraftFutureStateChangesPayload,
  { drafts, persisted }: FutureStateDraftExecutionState,
  inputs: DraftChangeInputs
): Applied => {
  if (!persisted) {
    return reject(
      'The claims of the future state are not loaded. Try again later.'
    );
  }
  const rejection = batchRejection(payload, drafts);
  if (rejection) return rejection;
  const draftIdByAlias = {
    ...drafts.draftIdByAlias,
    ...Object.fromEntries(
      [...payload.claimGroups, ...payload.claims]
        .flatMap(aliasOf)
        .map(alias => [alias, inputs.newId()])
    ),
  };
  const context: ChangeContext = {
    ...inputs,
    resolve: alias => draftIdByAlias[alias] ?? alias,
    futureStateId: drafts.futureStateId,
  };
  const persistedById: PersistedById = {
    claims: keyBy(persisted.claims, '_id'),
    claimGroups: keyBy(persisted.claimGroups, '_id'),
  };
  const result = [
    ...payload.claimGroups.map(change =>
      applyChange(
        'claimGroups',
        persistedById.claimGroups,
        toGroupChange(change, context)
      )
    ),
    ...payload.claims.map(change =>
      applyChange(
        'claims',
        persistedById.claims,
        toClaimChange(change, context)
      )
    ),
  ].reduce<Applied>(
    (applied, step) =>
      applied.status === 'rejected'
        ? applied
        : step(withNextDraftVersion(applied.payload)),
    valid({ ...drafts, draftIdByAlias })
  );
  if (result.status === 'rejected') return result;
  return (
    resultRejection(
      drafts,
      result.payload,
      persistedById.claimGroups,
      draftIdByAlias
    ) ?? result
  );
};
